/**
 * Linha de mensagem — a única do Localizador que não tem 34px fixos.
 *
 * Ela cresce porque leva DUAS informações: o trecho casado e a atribuição
 * («quem · quando»). A atribuição não é enfeite: busca por substring produz
 * ruído legítimo — o termo «mari» casa «marido» —, e sem ela o operador não
 * descarta o falso positivo sem abrir a conversa.
 */
import { MessageCircle } from 'lucide-react';

import { HighlightTerm } from './HighlightTerm';

import type { SpotlightItem } from './types';

export function SpotlightMessageRow({
  item,
  focused,
  onOpen,
  term = '',
}: {
  item: SpotlightItem;
  focused: boolean;
  onOpen: () => void;
  /** Termo digitado, para realçar o trecho casado. */
  term?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`spotlight__row spotlight__row--msg${focused ? ' spotlight__row--focused' : ''}`}
      onClick={onOpen}
      tabIndex={-1}
      data-testid={`spotlight-row-${item.key}`}
      aria-current={focused ? 'true' : undefined}
    >
      <span className="spotlight__row-icon">
        <MessageCircle size={14} aria-hidden />
      </span>
      <span className="spotlight__msg">
        <span className="spotlight__msg-excerpt">
          <HighlightTerm text={item.excerpt ?? item.label} term={term} />
        </span>
        {item.attribution !== undefined && item.attribution !== '' && (
          <span className="spotlight__msg-attribution">{item.attribution}</span>
        )}
      </span>
    </button>
  );
}

/**
 * Esqueleto da seção lenta. Duas linhas de altura equivalente a dois resultados,
 * para que a chegada dos dados NÃO empurre o que está abaixo.
 *
 * `display:block` no esqueleto é obrigatório — como `<span>` inline, height e
 * width são ignorados e o esqueleto fica invisível (defeito pego na verificação
 * visual da rodada 1 de design).
 */
export function SpotlightMessageSkeleton(): JSX.Element {
  return (
    <>
      {[0, 1].map((i) => (
        <div key={i} className="spotlight__row spotlight__row--msg" aria-hidden>
          <span className="spotlight__row-icon" style={{ opacity: 0.4 }}>
            <MessageCircle size={14} aria-hidden />
          </span>
          <span className="spotlight__msg">
            <span className="spotlight__skel" style={{ width: i === 0 ? '88%' : '72%' }} />
            <span
              className="spotlight__skel"
              style={{ width: i === 0 ? '38%' : '30%', marginTop: 6, height: 8 }}
            />
          </span>
        </div>
      ))}
    </>
  );
}
